/* Desert · motion runtime — reveals + spring-damped hero parallax.
   The CSS does all the heavy lifting; this script only assigns
   data-attributes and lerps two CSS variables. */
(function(){
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // 1. Auto-tag reveal targets — kept in one place so motion
  //    behavior is auditable without scanning markup.
  const REVEAL = [
    '.featured h2', '.featured p', '.featured .specs', '.featured .price-row',
    '.featured .inner > div:first-child .cta-row',
    '.best-head h2', '.best-grid .pcard',
    '.occ-head h2', '.occ-head .aside', '.occ-rail .occ-card',
    '.concierge h3', '.concierge p', '.concierge .wa',
    '.story h2', '.story p', '.story .sign',
    '.press h2', '.press .logos > div', '.quotes .quote-card',
    '.foot-headline'
  ];
  const REVEAL_CLIP = [
    '.featured .img-stack',
    '.concierge .visual',
    '.story .visual'
  ];
  REVEAL.forEach(sel => document.querySelectorAll(sel).forEach(el =>
    el.setAttribute('data-reveal', '')
  ));
  REVEAL_CLIP.forEach(sel => document.querySelectorAll(sel).forEach(el =>
    el.setAttribute('data-reveal-clip', '')
  ));

  // 2. IntersectionObservers — flip the attribute to "in"; CSS does the rest.
  //    Two observers with different sensitivities:
  //      • io_copy fires early so headlines/copy reveal as the section's
  //        top edge slides into view (low threshold, small -80px margin).
  //      • io_clip fires late so visual reveals — the featured image
  //        scale-fade and the seal's stamp-impress — only play once the
  //        user is actually looking at them. A small threshold combined
  //        with a large bottom rootMargin means "wait until the element
  //        crosses well into the viewport before triggering".
  const flip = attr => e => {
    if (!e.isIntersecting) return;
    e.target.setAttribute(attr, 'in');
    e.target._io.unobserve(e.target);
  };
  const io_copy = new IntersectionObserver(
    es => es.forEach(flip('data-reveal')),
    { rootMargin: '0px 0px -80px 0px', threshold: 0.05 }
  );
  // For .reveal-clip we want the trigger to fire when the element is
  // *meaningfully visible* — not the moment a pixel enters the bottom
  // of the screen, but also not so late that fast scrollers miss it.
  // Threshold 0 + a -35% bottom rootMargin lands the trigger when the
  // first pixel of the image-stack crosses into the top 65% of the
  // viewport, which is reliably "in the user's eyeline" across both
  // smooth-scroll and momentum-scroll on iOS.
  const io_clip = new IntersectionObserver(
    es => es.forEach(flip('data-reveal-clip')),
    { rootMargin: '0px 0px -35% 0px', threshold: 0 }
  );
  document.querySelectorAll('[data-reveal]').forEach(el => {
    el._io = io_copy; io_copy.observe(el);
  });
  document.querySelectorAll('[data-reveal-clip]').forEach(el => {
    el._io = io_clip; io_clip.observe(el);
  });

  // 2b. Price count-up — when a price scrolls into view, ease from 0 to
  //     the printed value. Inherits the card's --reveal-delay so prices
  //     count up in sync with each card's fade-in stagger. Plays once.
  //     The pcard markup is `<div class="price">480 <small>AED</small></div>`,
  //     so we mutate only the leading text node and leave the <small> alone.
  const priceEls = document.querySelectorAll('.featured .price-row .num, .pcard .price');
  priceEls.forEach(el => {
    const node = [...el.childNodes].find(n => n.nodeType === 3 && /\d/.test(n.textContent));
    if (!node) return;
    const raw = node.textContent;
    const target = parseInt(raw.replace(/\D/g, ''), 10);
    if (!target) return;
    const suffix = (raw.match(/\d+(\D*)$/) || ['', ''])[1];
    el._price = { node, target, suffix, raw };
    node.textContent = '0' + suffix;
  });
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
  const io_price = new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) return;
    io_price.unobserve(e.target);
    const { node, target, suffix } = e.target._price;
    const delay = parseFloat(getComputedStyle(e.target).getPropertyValue('--reveal-delay')) || 0;
    const duration = 550;
    const t0 = performance.now() + delay;
    const tick = now => {
      const t = Math.min(1, Math.max(0, (now - t0) / duration));
      node.textContent = Math.round(easeOutCubic(t) * target) + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }), { rootMargin: '0px 0px -80px 0px', threshold: 0.05 });
  priceEls.forEach(el => { if (el._price) io_price.observe(el); });

  // 3. Branch anchor — keep the decorative video's "stem base" aligned
  //    with the top of the .meta-row line, in any viewport size.
  //    We measure the gap between the meta-row's top edge and the hero's
  //    bottom edge, then expose it as --branch-y. The CSS does the rest:
  //    the video's bottom edge sits at that line, then translateY(% of
  //    its own height) shifts it down so the empty bottom-padding inside
  //    the clip falls below the line — leaving the stem on the line.
  //
  //    On mobile (≤720px) the layout reflows and the stem instead grows
  //    from the .strip band that follows the hero, so we clear the inline
  //    --branch-y and let the CSS rule (0px) take effect.
  {
    const heroEl  = document.querySelector('.hero');
    const metaRow = document.querySelector('.hero-duo .meta-row');
    const branch  = document.querySelector('.hero .bg-media');
    const mobileMQ = window.matchMedia('(max-width: 720px)');
    if (heroEl && metaRow && branch){
      const writeBranch = () => {
        if (mobileMQ.matches){
          heroEl.style.removeProperty('--branch-y');
          return;
        }
        const hr = heroEl.getBoundingClientRect();
        const mr = metaRow.getBoundingClientRect();
        heroEl.style.setProperty('--branch-y', (hr.bottom - mr.top).toFixed(1) + 'px');
      };
      writeBranch();
      window.addEventListener('resize', writeBranch);
      // Recompute after fonts load (display font shifts h1 height,
      // which shifts the meta-row down).
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(writeBranch);
      // And once the video reports its intrinsic dimensions, in case
      // anything in the chain depended on it.
      branch.addEventListener('loadedmetadata', writeBranch, { once: true });
    }
  }

  // 3b. Mobile: the top (flipped) branch hangs 120px off-screen right,
  //     so the first ~3s of its growth animation are hidden — on a
  //     small screen you'd see the plant slowly creeping in. Skip the
  //     playhead past that intro so the foliage starts already grown.
  //     We do this only when the mobile MQ matches at load time;
  //     once a video starts playing, jumping it later would be jarring.
  {
    const flipVid = document.querySelector('.hero .bg-media.flip');
    if (flipVid && window.matchMedia('(max-width: 720px)').matches){
      const SKIP_TO = 2.5; // seconds — past the grow-in animation
      const seek = () => {
        // Clamp in case the source is ever shorter than expected.
        const d = isFinite(flipVid.duration) ? flipVid.duration : SKIP_TO;
        try { flipVid.currentTime = Math.min(SKIP_TO, Math.max(0, d - 0.05)); } catch (_) {}
      };
      // readyState >= 1 = HAVE_METADATA — duration is known, we can seek.
      if (flipVid.readyState >= 1) seek();
      else flipVid.addEventListener('loadedmetadata', seek, { once: true });
    }
  }

  // 4. Hero parallax — two input channels, one element.
  //    a) Spring-damped mouse offset (pointer-fine only) → --px / --py
  //    b) Raw scroll position (all devices)             → --sy
  //    On mobile, CSS resets translate on .a/.b inside the responsive
  //    rules so the parallax effectively has no visual effect.
  const imgs = document.querySelector('.hero-duo .imgs');
  const hero = document.querySelector('.hero-duo');

  if (imgs){
    if (matchMedia('(hover: hover) and (pointer: fine)').matches){
      let tx = 0, ty = 0, cx = 0, cy = 0, raf = null;
      const tick = () => {
        cx += (tx - cx) * 0.08;
        cy += (ty - cy) * 0.08;
        imgs.style.setProperty('--px', cx.toFixed(3));
        imgs.style.setProperty('--py', cy.toFixed(3));
        if (Math.abs(tx - cx) > 0.0008 || Math.abs(ty - cy) > 0.0008){
          raf = requestAnimationFrame(tick);
        } else {
          raf = null;
        }
      };
      window.addEventListener('mousemove', (e) => {
        const r = (hero || imgs).getBoundingClientRect();
        if (e.clientY < r.top - 200 || e.clientY > r.bottom + 200) return;
        const ox = (e.clientX - (r.left + r.width  / 2)) / (r.width  / 2);
        const oy = (e.clientY - (r.top  + r.height / 2)) / (r.height / 2);
        tx = Math.max(-1, Math.min(1, ox));
        ty = Math.max(-1, Math.min(1, oy));
        if (raf === null) raf = requestAnimationFrame(tick);
      }, { passive: true });
      window.addEventListener('mouseleave', () => {
        tx = 0; ty = 0;
        if (raf === null) raf = requestAnimationFrame(tick);
      });
    }

    let scrollRaf = null;
    const writeScroll = () => {
      scrollRaf = null;
      const r = hero.getBoundingClientRect();
      if (r.bottom < -200) return;
      imgs.style.setProperty('--sy', Math.max(0, -r.top).toFixed(1));
    };
    window.addEventListener('scroll', () => {
      if (scrollRaf === null) scrollRaf = requestAnimationFrame(writeScroll);
    }, { passive: true });
    writeScroll();
  }
})();
